<template>
  <header ref="header" class="card-header" :class="{ center: 'x-number' }">
    <span class="card-title">{{ props.widget.title }}</span>
    <div class="right-buttons">
      <v-menu
        v-model="showSettingsMenu"
        class="pl-5 chart-settings-menu"
        :close-on-content-click="false"
        location="end"
      >
        <template #activator="{ props: activatorProps }">
          <legend-btn class="x-btn" v-bind="activatorProps" />
        </template>
        <v-card
          data-test-id="chart-widget-card-id"
          class="mx-auto chart-widget-card overflow-auto pr-2 pl-2"
        >
          <v-card-title class="pt-5 pb-3">{{ labels.DASHBOARD.WIDGET_CONFIGURATION }}</v-card-title>
          <v-card-item class="pb-3 pl-5">
            <v-row class="pt-5 pb-2">
              <v-col cols="4" class="pr-0 pt-1">
                <span class="text-style">{{ labels.DASHBOARD.HEADING }}</span>
              </v-col>
              <v-col cols="6" class="pl-0 pr-0 pt-0">
                <v-text-field
                  id="new-chart-title-field"
                  v-model="titleModel"
                  class="new-chart-title-field-class"
                  variant="outlined"
                  single-line
                  density="compact"
                  :placeholder="props.widget.chart?.defaultTitle ?? ''"
                  :hide-details="true"
                  clearable
                  @click:clear="titleModel = ''"
                />
              </v-col>
            </v-row>
            <v-row class="pb-3">
              <v-col cols="4" class="pr-0 pt-1">
                <span class="text-style">{{ labels.DASHBOARD.VISUALIZATION }}</span>
              </v-col>
              <v-col cols="6" class="pl-0 pr-0 pt-0">
                <v-select
                  id="select-type-id"
                  v-model="chartTypeModel"
                  class="select-chart-type-class"
                  :items="chartTypeItems"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  :hide-details="true"
                />
              </v-col>
              <v-spacer />
            </v-row>

            <v-row class="pb-0">
              <v-col cols="3 pb-0">
                <span class="text-style">{{ labels.DASHBOARD.DATA }}</span>
              </v-col>
            </v-row>

            <v-row class="d-flex justify-end pt-2">
              <v-col cols="8" class="pl-2 pt-0">
                <v-checkbox
                  v-model="allLegendItemsSelected"
                  :label="labels.DASHBOARD.SELECT_ALL"
                  density="compact"
                  :hide-details="true"
                  @click="manageLegendItemsVisibility"
                />
              </v-col>
              <v-col cols="4" class="pt-0">
                <v-switch
                  v-model="showLabelsModel"
                  class="switch-labels-class pl-2"
                  color="black"
                  density="compact"
                  :label="labels.DASHBOARD.CHART_LABELS"
                  :hide-details="true"
                  :disabled="props.widget.chartType === 'table'"
                />
              </v-col>
            </v-row>

            <v-virtual-scroll class="pb-2" :items="chartLegendItems" height="300" item-height="50">
              <template #default="{ item }">
                <v-checkbox
                  v-model="hiddenSeriesModel"
                  class="pt-0 pl-5 pb-0 legend-item-class"
                  density="compact"
                  :hide-details="true"
                  :label="item.name"
                  :true-value="item.name"
                  :false-value="item.name"
                  :style="
                    !props.widget.hiddenSeries.includes(item.name ?? '')
                      ? {
                          color: item.color,
                        }
                      : {}
                  "
                  :false-icon="constants.ICONS_MDI.ICON_CHECKBOX_MARKED"
                  :true-icon="constants.ICONS_MDI.ICON_CHECKBOX_BLANK"
                  @click="checkIfAllSelected"
                />
              </template>
            </v-virtual-scroll>
            <v-row class="d-flex justify-center pt-3 pb-2">
              <v-btn data-test-id="delete-widget-button" variant="plain" @click="removeChart()">
                <span class="delete-widget-btn-text text-style">
                  {{ labels.DASHBOARD.DELETE_WIDGET }}
                </span>
              </v-btn>
            </v-row>
          </v-card-item>
        </v-card>
      </v-menu>
    </div>
  </header>

  <v-container class="dashboard_chart_container">
    <div
      v-if="
        chartOptions &&
        widget.chartType === 'table' &&
        computedTableChartOptions.headers &&
        computedTableChartOptions.items
      "
      id="listContainer"
      class="list-container-table-chart"
      style="height: calc(100% - 10px)"
    >
      <analyze-data-table
        table-id="chart-widget-table-id"
        fixed-header
        :headers="computedTableChartOptions.headers"
        :items="computedTableChartOptions.items"
        :has-multisort="true"
        :total-items="computedTableChartOptions.items.length"
        @select="showDetailsForTablesEntry"
      />
    </div>

    <ECharts
      v-else-if="chartOptions && (widget.chartType === 'bar' || widget.chartType === 'pie')"
      class="echart"
      :option="chartOptions"
      autoresize
      :update-options="{ notMerge: true }"
      @click="showDetailsForEChart"
    />

    <div v-else-if="loading" class="d-flex justify-center align-center h-100 pb-15">
      <LoaderCircular />
    </div>
  </v-container>

  <v-menu
    v-for="([key, properties], i) in detailsMenus"
    :key="i"
    ref="detailsCard"
    v-model="properties.isShown"
    transition="scale-transition"
    :close-on-content-click="false"
    :no-click-animation="true"
    persistent
    :style="properties.isSelected ? `${detailsMenusPosition} z-index: 1500;` : detailsMenusPosition"
    @mouseup="setSelectedMenuProperty(properties)"
  >
    <ChartDetailsView
      v-if="props.widget.chart"
      :current-chart-details="props.widget"
      :current-series-name="properties.seriesName"
      :view-index="i"
      :chart-index="props.chartIndex"
      :grid-item-id="gridItemId"
      :server-items-length="serverItemsLength"
      @close-chart-details="closeDetails(key)"
    />
  </v-menu>
</template>

<script lang="ts" setup>
  import ECharts from 'vue-echarts'
  import { CanvasRenderer } from 'echarts/renderers'
  import { use } from 'echarts/core'
  import { BarChart, PieChart, TreemapChart } from 'echarts/charts'
  import { EChartData, TableChartOptions } from '@/models/chart.model'
  import LoaderCircular from '@/components/Loader/LoaderCircular/LoaderCircular.vue'

  // import ECharts modules manually to reduce bundle size
  import {
    GridComponent,
    TooltipComponent,
    TitleComponent,
    LegendComponent,
  } from 'echarts/components'

  import { ChartOptionsType } from '@/models/chart.model'

  import { computed, ref, watch } from 'vue'
  import labels from '@/translations/labels.json'
  import constants from '@/constants/Config.constants'
  import { useDashboardStore } from '@/stores/dashboardStore'

  import ChartDetailsView from '../ChartDetailsView/ChartDetailsView.vue'
  import LegendBtn from '@/components/DashboardChartActionButtons/legend-btn.vue'
  import { ChartType, WidgetProperties } from '@/models/dashboard.model'
  import { useEventBus } from '@/utils/events'
  import { ECElementEvent } from 'echarts'
  import { onMounted } from 'vue'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  use([
    BarChart,
    PieChart,
    TreemapChart,
    CanvasRenderer,
    GridComponent,
    TitleComponent,
    LegendComponent,
    TooltipComponent,
  ])

  // Component types
  interface MenuProperties {
    zIndex: number
    isShown: boolean
    seriesName: string
    isSelected: boolean
  }

  interface ChartLegendItem {
    name: string
    color: string
  }

  interface ChartTypeItem {
    title: string
    value: ChartType
  }

  // Component constants
  const SMALLER_Z_INDEX = 2000
  const LARGER_Z_INDEX = 3000

  // Props
  const props = defineProps({
    widget: { type: Object as () => WidgetProperties, required: true },
    chartIndex: { type: Number, required: true },
    xGridOffset: { type: Number, required: true },
    yGridOffset: { type: Number, required: true },
    gridItemId: { type: String, required: true },
  })

  // Stores
  const dashboardStore = useDashboardStore()

  // Services
  const colorConverterService = injectTyped(ServiceKeys.colorConverter)
  const dashboardService = injectTyped(ServiceKeys.dashboard)
  const chartService = injectTyped(ServiceKeys.chart)

  /// General view
  const chartOptions = ref<ChartOptionsType>()
  const serverItemsLength = ref<number>(0)
  const loading = ref(true)

  const allLegendItemsSelected = ref(true)

  /// Settings menu
  const showSettingsMenu = ref(false)
  const chartTypeItems = ref<ChartTypeItem[]>([
    { title: labels.DASHBOARD.PIE_CHART, value: 'pie' },
    { title: labels.DASHBOARD.BAR_CHART, value: 'bar' },
    { title: labels.DASHBOARD.TABLE, value: 'table' },
  ])

  /// Details menu
  const detailsMenus = ref(new Map<string, MenuProperties>())
  const detailsMenusPosition = ref<string>('')

  // Computed
  const chartLegendItems = computed<ChartLegendItem[]>(() => {
    return (
      props.widget.chart?.data?.map((item) => ({
        name: item.name,
        color: colorConverterService.convertArtifactTypeColorToCssColor(item.color, item.name),
      })) ?? []
    )
  })

  const hiddenSeriesModel = computed({
    get: () => props.widget.hiddenSeries,
    set: (hiddenSeries: string[]) => {
      dashboardStore.updateWidgetProperties(props.widget.id, { hiddenSeries })
    },
  })

  const chartTypeModel = computed({
    get: () => props.widget.chartType,
    set: (chartType: ChartType) => {
      dashboardStore.updateWidgetProperties(props.widget.id, { chartType })
    },
  })

  const titleModel = computed({
    get: () => props.widget.title,
    set: (title: string) => {
      dashboardStore.updateWidgetProperties(props.widget.id, { title })
    },
  })

  const showLabelsModel = computed({
    get: () => props.widget.configuration.showLabels ?? true,
    set: (showLabels: boolean) => {
      dashboardStore.updateWidgetProperties(props.widget.id, {
        configuration: { ...props.widget.configuration, showLabels },
      })
    },
  })

  const computedTableChartOptions = computed(() => {
    if (isTableChartOptions(chartOptions.value)) {
      return chartOptions.value
    } else {
      // Handle the case where chartOptions is not a TableChartOptions,
      // perhaps by returning a default value or logging a warning.
      return { headers: [], items: [] } // Example default value
    }
  })

  function isTableChartOptions(obj: any): obj is TableChartOptions {
    return obj && typeof obj === 'object' && 'headers' in obj && 'items' in obj
  }

  onMounted(() => {
    allLegendItemsSelected.value =
      chartLegendItems.value.length - hiddenSeriesModel.value.length === 0
  })

  // Events
  useEventBus('sidebarToggled', (offset: number) => {
    detailsMenusPosition.value = `left:${offset}px; top:${props.yGridOffset}px; bottom:0px; right:0px`
  })

  // Watchers
  watch(
    [
      () => props.widget,
      () => props.widget.chart,
      () => props.widget.chartType,
      () => props.widget.hiddenSeries,
      () => props.widget.configuration,
    ],
    async ([newWidget, newChart]) => {
      if (newChart) {
        loading.value = true

        try {
          await dashboardStore.loadDataForChartFromApi({
            dashboardService,
            chart: newChart,
          })
        } finally {
          loading.value = false
        }

        try {
          chartOptions.value = chartService.computeChartOptions(newWidget)
        } catch {
          return
        }
      }
    },
    {
      immediate: true,
    },
  )

  // Methods
  function removeChart(): void {
    dashboardStore.removeWidget(props.widget)
  }

  function setSelectedMenuProperty(properties: MenuProperties): void {
    Array.from(detailsMenus.value.values()).forEach((menuProperty) => {
      menuProperty.isSelected = false
      menuProperty.zIndex = SMALLER_Z_INDEX
    })

    properties.isSelected = true
    properties.zIndex = LARGER_Z_INDEX
  }

  function hasCount(obj: any): obj is { count: number } {
    return obj && 'count' in obj
  }

  function hasName(obj: any): obj is { name: string } {
    return obj && 'name' in obj
  }

  async function showDetailsForTablesEntry(tableRow: any): Promise<void> {
    if (tableRow === undefined) {
      return
    }

    if (hasCount(tableRow) && hasName(tableRow)) {
      serverItemsLength.value = +tableRow.count

      /* Prevent the dialog from being shown if the detailsPath does not exist
       * This is needed to prevent a backend call with a non-existing url and to prevent the chart details window to be visible for the user.
       * Backend has now no logic to handle all the metrics.
       * */

      if (!props.widget.chart?.detailsPath) {
        return
      }

      detailsMenusPosition.value = `left:${props.xGridOffset}px; top:${props.yGridOffset}px; bottom:0px; right:0px;`
      detailsMenus.value.set(tableRow.name, {
        isShown: true,
        seriesName: tableRow.name,
        isSelected: false,
        zIndex: SMALLER_Z_INDEX,
      })

      const menuProperties = detailsMenus.value.get(tableRow.name)
      if (menuProperties) {
        setSelectedMenuProperty(menuProperties)
      }
    }
  }

  function showDetailsForEChart(data: EChartData | ECElementEvent): void {
    if (!data.value) {
      return
    }

    serverItemsLength.value = +data.value

    /* Prevent the dialog from being shown if the detailsPath does not exist
     * This is needed to prevent a backend call with a non-existing url and to prevent the chart details window to be visible for the user.
     * Backend has now no logic to handle all the metrics.
     */
    if (!props.widget.chart?.detailsPath) {
      return
    }

    if (
      data.event === undefined ||
      !('pageY' in data.event.event) ||
      !('pageX' in data.event.event)
    ) {
      return
    }

    detailsMenusPosition.value = `left:${props.xGridOffset}px; top:${props.yGridOffset}px; bottom:0px; right:0px;`
    detailsMenus.value.set(data.name, {
      isShown: true,
      seriesName: data.name,
      isSelected: false,
      zIndex: SMALLER_Z_INDEX,
    })

    const menuProperties = detailsMenus.value.get(data.name)
    if (menuProperties) {
      setSelectedMenuProperty(menuProperties)
    }
  }

  function enableAllLegendItems(): void {
    dashboardStore.updateWidgetProperties(props.widget.id, { hiddenSeries: [] })
  }

  function disableAllLegendItems(): void {
    const allSeries = props.widget.chart?.data?.map((item) => item.name)
    dashboardStore.updateWidgetProperties(props.widget.id, { hiddenSeries: allSeries })
  }

  function manageLegendItemsVisibility(): void {
    if (allLegendItemsSelected.value) {
      disableAllLegendItems()
      allLegendItemsSelected.value = false
    } else {
      enableAllLegendItems()
      allLegendItemsSelected.value = true
    }
  }

  function checkIfAllSelected(): void {
    if (chartLegendItems.value.length - hiddenSeriesModel.value.length !== 0) {
      allLegendItemsSelected.value = false
    }
  }

  function closeDetails(detailsName: string): void {
    detailsMenus.value.set(detailsName, {
      isShown: false,
      seriesName: '',
      isSelected: false,
      zIndex: SMALLER_Z_INDEX,
    })
  }
</script>

<style lang="scss" scoped>
  .enable-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .list-container-table-chart {
    overflow: auto;
  }

  .easy-table {
    --easy-table-body-row-hover-background-color: rgb(var(--v-theme-itemis-analyze-secondary));
    --easy-table-body-row-background-color: rgb(var(--v-theme-grey-light));
    --easy-table-body-row-font-color: rgb(var(--v-theme-font-color));
    --easy-table-body-even-row-font-color: rgb(var(--v-theme-font-color));
    height: inherit;
  }

  .disable-btn {
    background-color: rgb(var(--v-theme-grey-dark));
    color: rgb(var(--v-theme-surface));
  }
  .text-style {
    font-weight: bold;
  }

  .delete-widget-btn-text {
    color: rgb(var(--v-theme-error));
  }

  .new-chart-title-field-class :deep(.v-field__input) {
    padding-top: 0px;
    padding-bottom: 0px;
    min-height: 32px;
  }

  .select-chart-type-class :deep(.v-field__input) {
    padding-top: 0px;
    padding-bottom: 0px;
    min-height: 32px;
  }

  .legend-item-class :deep(.v-input__control) {
    max-height: 35px;
  }

  .switch-labels-class {
    max-width: 120px;
  }

  .switch-labels-class :deep(.v-label) {
    order: 0;
    max-width: 70px;
  }

  .switch-labels-class :deep(.v-selection-control__wrapper) {
    order: 1;
  }

  .set-title-btn {
    min-width: 35px;
    min-height: 35px;
    max-width: 35px;
    max-height: 35px;
    border-radius: 5px;
  }
  .chart-widget-card {
    overflow: auto;
    scrollbar-width: none;
    min-width: 450px;
    min-height: 300px;
  }
  .dashboard_chart_container {
    height: 100%;
    width: 100%;
    padding: 10px;
    margin: initial;
  }

  .echart {
    width: 100%;
    height: 100%;
    padding: 2%;
  }

  .chartHeader {
    max-height: 20px;
  }

  #empty_series {
    position: absolute;
    left: 30em;
    top: 10em;
  }

  .slice-wrapper {
    width: 100%;
    height: 100%;
  }
  .card-header {
    font-size: 16px;
    color: rgb(var(--v-theme-grey-dark));
    height: 30px;
    line-height: 30px;
    text-align: left;
    position: relative;
    padding: 0 5px;
    &.center {
      text-align: center;
    }

    .right-buttons {
      height: 30px;
      float: right;
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: row;
      text-align: right;

      .x-btn {
        width: 20px;
      }
    }
  }

  .chart-settings-menu {
    z-index: 2000 !important;
  }
</style>
